// Artist management service
import { supabase } from './supabase';
import { noderr, DatabaseError, ValidationError } from '../../shared/utils/errorHandler';
import type { 
  Artist, 
  CreateArtistData, 
  UpdateArtistData, 
  ArtistsFilter,
  ArtistStats
} from '../../shared/types/entities';

export class ArtistService {
  // Get all artists with optional filtering
  async getArtists(filter: ArtistsFilter = {}): Promise<Artist[]> {
    let query = supabase
      .from('artists')
      .select('*')
      .or('archived.is.null,archived.eq.false'); // Only show non-archived artists

    // Apply client filter
    if (filter.client_id) {
      query = query.eq('client_id', filter.client_id);
    }

    // Apply genre filter
    if (filter.genre) {
      query = query.eq('genre', filter.genre);
    }

    // Apply search filter
    if (filter.search) {
      query = query.ilike('name', `%${filter.search}%`);
    }

    // Apply sorting
    const sortBy = filter.sortBy || 'created_at';
    const sortOrder = filter.sortOrder || 'desc';
    query = query.order(sortBy, { ascending: sortOrder === 'asc' });

    const { data: artists, error } = await query;
    
    if (error) throw new DatabaseError(`Failed to fetch artists: ${error.message}`);
    
    if (!artists || artists.length === 0) {
      return [];
    }

    // Map primary_contact_id to client_id for frontend compatibility
    return artists.map((artist: any) => ({
      ...artist,
      client_id: artist.primary_contact_id || artist.client_id
    }));
  }

  // Get single artist by ID
  async getArtistById(id: string): Promise<Artist | null> {
    const { data, error} = await supabase
      .from('artists')
      .select(`
        *,
        client:contacts!primary_contact_id(*)
      `)
      .eq('id', id)
      .single();
    
    if (error) {
      if (error.code === 'PGRST116') return null; // Not found
      throw new DatabaseError(`Failed to fetch artist: ${error.message}`);
    }
    
    // Map primary_contact_id to client_id for frontend compatibility
    if (data) {
      (data as any).client_id = (data as any).primary_contact_id || (data as any).client_id;
    }
    
    return data || null;
  }

  // Create new artist
  async createArtist(artistData: CreateArtistData): Promise<Artist> {
    // Validate required fields
    if (!artistData.name?.trim()) {
      throw new ValidationError('Artist name is required');
    }

    if (!artistData.client_id) {
      throw new ValidationError('Client is required');
    }

    // Verify client exists
    const { data: clientData, error: clientError } = await supabase
      .from('contacts')
      .select('id')
      .eq('id', artistData.client_id)
      .single();

    if (clientError || !clientData) {
      throw new ValidationError('Selected client does not exist');
    }

    // Check for duplicate artist name across all clients
    const { data: dupData, error: dupError } = await supabase
      .from('artists')
      .select('id, name')
      .ilike('name', artistData.name.trim())
      .maybeSingle();

    if (!dupError && dupData) {
      throw new ValidationError(`An artist named "${artistData.name.trim()}" already exists.`);
    }

    const { data, error } = await supabase
      .from('artists')
      .insert({
        client_id: artistData.client_id,
        name: artistData.name.trim(),
        genre: artistData.genre?.trim() || null,
        spotify_url: artistData.spotify_url?.trim() || null,
        spotify_id: artistData.spotify_id?.trim() || null,
        spotify_data: artistData.spotify_data || null,
        image_url: artistData.image_url?.trim() || null,
        followers_count: artistData.followers_count || null,
        lastfm_data: artistData.lastfm_data || null
      })
      .select(`
        *,
        client:contacts(*)
      `)
      .single();
    
    if (error) {
      throw new DatabaseError(`Failed to create artist: ${error.message}`);
    }
    
    // Map clients to client for consistency
    if (data && (data as any).contacts) {
      (data as any).client = (data as any).contacts;
      delete (data as any).contacts;
    }
    
    return data;
  }

  // Update existing artist
  async updateArtist(id: string, updates: UpdateArtistData): Promise<Artist> {
    // Clean up data
    const cleanUpdates: any = {};
    if (updates.name !== undefined) cleanUpdates.name = updates.name.trim();
    if (updates.genre !== undefined) cleanUpdates.genre = updates.genre?.trim() || null;
    if (updates.spotify_url !== undefined) cleanUpdates.spotify_url = updates.spotify_url?.trim() || null;
    if (updates.spotify_id !== undefined) cleanUpdates.spotify_id = updates.spotify_id?.trim() || null;
    if (updates.spotify_data !== undefined) cleanUpdates.spotify_data = updates.spotify_data;
    if (updates.image_url !== undefined) cleanUpdates.image_url = updates.image_url?.trim() || null;
    if (updates.followers_count !== undefined) cleanUpdates.followers_count = updates.followers_count;
    if (updates.lastfm_data !== undefined) cleanUpdates.lastfm_data = updates.lastfm_data;
    
    if (updates.client_id !== undefined) {
      // Verify new client exists if changing client
      const { data: clientData, error: clientError } = await supabase
        .from('contacts')
        .select('id')
        .eq('id', updates.client_id)
        .single();

      if (clientError || !clientData) {
        throw new ValidationError('Selected client does not exist');
      }
      cleanUpdates.client_id = updates.client_id;
    }

    const { data, error } = await supabase
      .from('artists')
      .update(cleanUpdates)
      .eq('id', id)
      .select(`
        *,
        client:contacts(*)
      `)
      .single();
    
    if (error) {
      if (error.code === 'PGRST116') {
        throw new ValidationError('Artist not found');
      }
      throw new DatabaseError(`Failed to update artist: ${error.message}`);
    }
    
    // Map clients to client for consistency
    if (data && (data as any).contacts) {
      (data as any).client = (data as any).contacts;
      delete (data as any).contacts;
    }
    
    return data;
  }

  // Archive artist (soft delete)
  async archiveArtist(id: string): Promise<void> {
    const { error } = await supabase
      .from('artists')
      .update({ 
        archived: true 
      })
      .eq('id', id);
    
    if (error) {
      throw new DatabaseError(`Failed to archive artist: ${error.message}`);
    }
  }

  // Restore archived artist
  async restoreArtist(id: string): Promise<void> {
    const { error } = await supabase
      .from('artists')
      .update({ 
        archived: false 
      })
      .eq('id', id)
      .eq('archived', true); // Only restore archived artists
    
    if (error) {
      throw new DatabaseError(`Failed to restore artist: ${error.message}`);
    }
  }

  // Get archived artists
  async getArchivedArtists(): Promise<Artist[]> {
    const { data, error } = await supabase
      .from('artists')
      .select(`
        *,
        client:contacts(*)
      `)
      .eq('archived', true)
      .order('created_at', { ascending: false });
    
    if (error) {
      throw new DatabaseError(`Failed to fetch archived artists: ${error.message}`);
    }
    
    // Map clients to client for consistency
    const mappedData = (data || []).map((item: any) => {
      if (item.contacts) {
        item.client = item.contacts;
        delete item.contacts;
      }
      return item;
    });
    
    return mappedData;
  }

  // Delete artist (permanent - admin only)
  async deleteArtist(id: string): Promise<void> {
    // Check if artist has associated releases
    const { data: releaseData, error: releaseError } = await supabase
      .from('releases')
      .select('id')
      .eq('artist_id', id)
      .limit(1);

    if (!releaseError && releaseData && releaseData.length > 0) {
      throw new ValidationError('Cannot delete artist with associated releases. Delete releases first.');
    }

    const { error } = await supabase
      .from('artists')
      .delete()
      .eq('id', id);
    
    if (error) {
      throw new DatabaseError(`Failed to delete artist: ${error.message}`);
    }
  }

  // Get artist statistics
  async getArtistStats(id: string): Promise<ArtistStats> {
    const { data, error } = await supabase
      .from('client_hierarchy')
      .select('*')
      .eq('artist_id', id);

    if (error) {
      console.warn('Failed to fetch artist stats, returning defaults:', error);
      return {
        releases_count: 0,
        campaigns_count: 0,
        total_budget: 0
      };
    }

    const statsData = data || [];
    const uniqueReleases = new Set(statsData.map((d: any) => d.release_id).filter(Boolean));
    const uniqueCampaigns = new Set(statsData.map((d: any) => d.campaign_id).filter(Boolean));
    const totalBudget = statsData.reduce((sum: number, d: any) => sum + (d.campaign_budget || 0), 0);

    return {
      releases_count: uniqueReleases.size,
      campaigns_count: uniqueCampaigns.size,
      total_budget: totalBudget
    };
  }

  // Get unique genres for filtering
  async getGenres(): Promise<string[]> {
    const { data, error } = await supabase
      .from('artists')
      .select('genre')
      .not('genre', 'is', null)
      .not('genre', 'eq', '');

    if (error) {
      console.warn('Failed to fetch genres:', error);
      return [];
    }

    const genres = [...new Set(data?.map((d: any) => d.genre).filter(Boolean))] as string[];
    return genres.sort();
  }
}

// Export singleton instance
export const artistService = new ArtistService();
